import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import { useAuth, useFeature } from '../../../context/AuthContext';
import { REQUEST_STATUS_TONE as STATUS_TONE, APP_STATUS_LABEL } from '../../../lib/requestStatus';

const MAX_ROWS = 3;
const OPEN = {
  access: new Set(['pending']),
  proposal: new Set(['pending_manager', 'pending_admin']),
  app: new Set(['pending', 'approved', 'provisioning', 'active', 'publish_requested']),
};

const isFeatureDisabled = (err) => err?.response?.data?.error === 'feature_disabled';

const toAccessRow = (r) => ({ key: `access-${r.id}`, kind: 'access', title: r.tool_name, statusLabel: STATUS_TONE[r.status]?.label || r.status, createdAt: r.created_at, href: '/requests#sub=mine' });
const toProposalRow = (r) => ({ key: `proposal-${r.id}`, kind: 'proposal', title: r.name, statusLabel: STATUS_TONE[r.status]?.label || r.status, createdAt: r.created_at, href: '/requests#sub=mine' });
const toAppRow = (r) => ({ key: `app-${r.id}`, kind: 'app', title: r.name, statusLabel: APP_STATUS_LABEL[r.status] || r.status, createdAt: r.created_at, href: `/build-app/${r.id}` });

/**
 * The caller's open requests across tool access, tool proposals and apps.
 * `proposals` is the shared useToolRequests() result (fetched once per page).
 *
 * status 'error' only when EVERY attempted source failed; `partial` is true
 * when some failed, so the card never claims "no open requests" from a
 * failed read.
 */
export default function useMyRequests({ proposals }) {
  const { user } = useAuth();
  const appsOn = useFeature('build_your_app');
  const [base, setBase] = useState({ loading: true, access: null, app: null });
  const [attempt, setAttempt] = useState(0);
  const proposalsRetry = proposals?.retry;
  const retry = useCallback(() => {
    setAttempt((n) => n + 1);
    if (proposalsRetry) proposalsRetry();
  }, [proposalsRetry]);

  useEffect(() => {
    if (!user?.id) return undefined;
    let active = true;
    setBase((b) => ({ ...b, loading: true }));
    const opts = { withCredentials: true };
    const get = (path) => axios.get(`${API_BASE_URL}${path}`, opts)
      .then((r) => ({ ok: true, rows: Array.isArray(r.data) ? r.data : [] }))
      .catch((err) => (isFeatureDisabled(err) ? { ok: true, rows: [] } : { ok: false, rows: [] }));
    Promise.all([
      get('/api/tool_access_requests/mine'),
      appsOn ? get('/api/app_requests/mine') : Promise.resolve(null),
    ]).then(([access, app]) => { if (active) setBase({ loading: false, access, app }); });
    return () => { active = false; };
  }, [user?.id, appsOn, attempt]);

  return useMemo(() => {
    const pStatus = proposals?.status || 'off';
    if (base.loading || pStatus === 'loading') return { status: 'loading', rows: [], partial: false, retry };

    const parts = [
      { ...base.access, rows: (base.access?.rows || []).filter((r) => OPEN.access.has(r.status)).map(toAccessRow) },
    ];
    if (base.app) {
      parts.push({ ...base.app, rows: base.app.rows.filter((r) => OPEN.app.has(r.status)).map(toAppRow) });
    }
    if (pStatus !== 'off') {
      parts.push({
        ok: pStatus === 'ready',
        rows: pStatus === 'ready'
          ? proposals.rows.filter((r) => r.user_id === user?.id && OPEN.proposal.has(r.status)).map(toProposalRow)
          : [],
      });
    }

    const ok = parts.filter((p) => p.ok);
    if (ok.length === 0) return { status: 'error', rows: [], partial: false, retry };
    const rows = ok.flatMap((p) => p.rows)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, MAX_ROWS);
    return { status: 'ready', rows, partial: ok.length < parts.length, retry };
  }, [base, proposals, user?.id, retry]);
}
